import * as React from 'react';
import { Input, FormGroup, Label } from 'reactstrap';
import classnames from 'classnames';

interface UrlTabProps {
  sourceUrl: string;
  validUrl: boolean;
  errorMessage: string;
  isSaving: boolean;
  onUrlChange: (url: string) => void;
  onUrlSubmit: () => void;
  isHostedImage?: (url: string) => boolean;
}

export const UrlTab: React.FC<UrlTabProps> = ({
  sourceUrl,
  validUrl,
  errorMessage,
  isSaving,
  onUrlChange,
  onUrlSubmit,
  isHostedImage
}) => {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewError, setPreviewError] = React.useState<boolean>(false);
  const [showCopyButton, setShowCopyButton] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (validUrl && sourceUrl) {
      setPreviewUrl(sourceUrl);
      setPreviewError(false);
    } else {
      setPreviewUrl(null);
      setPreviewError(false);
    }
  }, [validUrl, sourceUrl]);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    onUrlChange(url);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && validUrl && !isSaving) {
      onUrlSubmit();
    }
  };

  const handleImageError = () => {
    setPreviewError(true);
  };

  const handleImageLoad = () => {
    setPreviewError(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here if needed
    }).catch((err) => {
    });
  };

  return (
    <div className="url-tab">
      <div className="url-header">
        <h5>Add image from URL</h5>
        <p className="text-muted">Enter a direct link to an image file</p>
      </div>

      <FormGroup>
        <Label for="imageUrl">Image URL</Label>
        <Input
          type="url"
          id="imageUrl"
          placeholder="https://example.com/image.jpg"
          value={sourceUrl}
          onChange={handleUrlChange}
          onKeyPress={handleKeyPress}
          className={classnames({
            'is-valid': validUrl && sourceUrl,
            'is-invalid': sourceUrl && !validUrl
          })}
          disabled={isSaving}
        />
        {sourceUrl && !validUrl && (
          <div className="invalid-feedback">
            Please enter a valid URL
          </div>
        )}
        {validUrl && sourceUrl && (
          <div className="valid-feedback">
            Valid URL format
          </div>
        )}
      </FormGroup>

      {previewUrl && (
        <div className="url-preview-section">
          <Label>Preview</Label>
          <div className="url-preview-container">
            {!previewError ? (
              <div>
                <img
                  src={previewUrl}
                  alt="URL Preview"
                  className="url-preview-image"
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                />
                {isHostedImage && !isHostedImage(previewUrl) && (
                  <div 
                    className="image-url-display"
                    onMouseEnter={() => setShowCopyButton(true)}
                    onMouseLeave={() => setShowCopyButton(false)}
                  >
                    <small className="text-muted url-text">{previewUrl}</small>
                    {showCopyButton && (
                      <button
                        className="btn btn-sm btn-outline-secondary copy-url-btn"
                        onClick={() => copyToClipboard(previewUrl)}
                        title="Copy URL"
                      >
                        <i className="fas fa-copy"></i> Copy
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="preview-error">
                <div className="error-icon">❌</div>
                <p>Unable to load image from this URL</p>
                <small className="text-muted">
                  Please check the URL and try again
                </small>
              </div>
            )}
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-danger">
          {errorMessage}
        </div>
      )}

      <div className="url-help">
        <h6>Tips for using image URLs:</h6>
        <ul>
          <small>
            <li>Use direct links to image files (ending in .jpg, .png, .gif, etc.)</li>
            <li>Ensure the image is publicly accessible</li>
            <li>Copy the image address, not the webpage URL</li>
            <li>Supported formats: JPG, PNG, GIF, WebP</li>
          </small>
        </ul>
      </div>
    </div>
  );
};